import React, { Suspense, lazy, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { PermissionProvider } from "./contexts/PermissionContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { theme } from "./utils/theme";
import "./index.css";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Loading } from "./components/Loading";
import ErrorBoundary from "./components/ErrorBoundary";
import { Permissions } from "./constants/permissions";
import { AppRoutes } from "./constants/appRoutes";

const Login = lazy(() =>
  import("./screens/login/Login").then((m) => ({ default: m.Login })),
);
const ResetPassword = lazy(() =>
  import("./screens/login/ResetPassword").then((m) => ({
    default: m.ResetPassword,
  })),
);
const PendingApproval = lazy(() =>
  import("./screens/pending-approval/PendingApproval").then((m) => ({
    default: m.PendingApproval,
  })),
);
const B2bSignUp = lazy(() =>
  import("./screens/signup/B2bSignUp").then((m) => ({ default: m.B2bSignUp })),
);
const Dashboard = lazy(() =>
  import("./screens/dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
const Catalog = lazy(() =>
  import("./screens/catalog/Catalog").then((m) => ({ default: m.Catalog })),
);
const ProductDetail = lazy(() =>
  import("./screens/product-detail/ProductDetail").then((m) => ({
    default: m.ProductDetail,
  })),
);
const TeamManagement = lazy(() =>
  import("./screens/team/TeamManagement").then((m) => ({
    default: m.TeamManagement,
  })),
);
const RequisitionList = lazy(() =>
  import("./screens/quotes/RequisitionList").then((m) => ({
    default: m.RequisitionList,
  })),
);
const CompanyLedger = lazy(() =>
  import("./screens/ledger/CompanyLedger").then((m) => ({
    default: m.CompanyLedger,
  })),
);
const Checkout = lazy(() =>
  import("./screens/checkout/Checkout").then((m) => ({ default: m.Checkout })),
);
const ActivityLog = lazy(() =>
  import("./screens/activity/ActivityLog").then((m) => ({
    default: m.ActivityLog,
  })),
);
const Orders = lazy(() =>
  import("./screens/orders/Orders").then((m) => ({ default: m.Orders })),
);
const PreOrders = lazy(() =>
  import("./screens/orders/PreOrders").then((m) => ({ default: m.PreOrders })),
);
const AddressBook = lazy(() =>
  import("./screens/addresses/AddressBook").then((m) => ({
    default: m.AddressBook,
  })),
);
const QuickOrder = lazy(() =>
  import("./screens/quick-order/QuickOrder").then((m) => ({
    default: m.QuickOrder,
  })),
);
const ApprovalSettings = lazy(() =>
  import("./screens/approval-settings/ApprovalSettings").then((m) => ({
    default: m.ApprovalSettings,
  })),
);
const FinancialHub = lazy(() => import("./screens/finance/FinancialHub"));
const Suppliers = lazy(() => import("./screens/suppliers/Suppliers"));
const SupplierDetail = lazy(() => import("./screens/suppliers/SupplierDetail"));
const ProfileSettings = lazy(() =>
  import("./screens/profile/ProfileSettings").then((m) => ({
    default: m.ProfileSettings,
  })),
);
const RMA = lazy(() => import("./screens/rma/RMA"));
const Notifications = lazy(() =>
  import("./screens/notifications/Notifications").then((m) => ({
    default: m.Notifications,
  })),
);
const Consignment = lazy(() =>
  import("./screens/consignment/SaleReport").then((m) => ({
    default: m.SaleReport,
  })),
);

const AuthEventInterceptor = () => {
  const { logout } = useAuth();
  useEffect(() => {
    const handleAuthRequired = async () => {
      console.warn(
        "401 Unauthorized event intercepted. Terminating connection.",
      );
      await logout();
    };
    window.addEventListener("authRequired", handleAuthRequired);
    return () => window.removeEventListener("authRequired", handleAuthRequired);
  }, [logout]);
  return null;
};

const AppContent = () => {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return <Loading message="Loading Enterprise Dashboard..." />;
  }

  return (
    <Suspense fallback={<Loading message="Loading assets..." />}>
      <Routes>
        <Route
          path={AppRoutes.LOGIN}
          element={
            isAuthenticated ? (
              <Navigate to={AppRoutes.DASHBOARD} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path={AppRoutes.SIGNUP}
          element={
            isAuthenticated ? (
              <Navigate to={AppRoutes.DASHBOARD} replace />
            ) : (
              <B2bSignUp />
            )
          }
        />
        <Route
          path={AppRoutes.RESET_PASSWORD}
          element={
            isAuthenticated ? (
              <Navigate to={AppRoutes.DASHBOARD} replace />
            ) : (
              <ResetPassword />
            )
          }
        />
        <Route
          path={AppRoutes.PENDING_APPROVAL}
          element={<PendingApproval />}
        />

        <Route
          element={
            <ProtectedRoute>
              <PermissionProvider>
                <Layout />
              </PermissionProvider>
            </ProtectedRoute>
          }
        >
          <Route
            path={AppRoutes.ROOT}
            element={<Navigate to={AppRoutes.DASHBOARD} replace />}
          />
          <Route
            path={AppRoutes.DASHBOARD}
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path={AppRoutes.PROFILE} element={<ProfileSettings />} />
          <Route
            path={AppRoutes.NOTIFICATIONS}
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CATALOG_1}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <Catalog mode="normal" />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRE_ORDER_ITEMS_1}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <Catalog mode="preorder" />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CATALOG}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRE_ORDER_ITEMS}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRODUCT}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.SUPPLIERS_1}
            element={
              <ProtectedRoute permission={Permissions.B2B_COMPANY_READ}>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.SUPPLIERS}
            element={
              <ProtectedRoute permission={Permissions.B2B_COMPANY_READ}>
                <SupplierDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CHECKOUT}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ORDERS_1}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ORDERS}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRE_ORDERS_1}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <PreOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRE_ORDERS}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <PreOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.QUOTES_1}
            element={
              <ProtectedRoute permission={Permissions.B2B_QUOTE_READ}>
                <RequisitionList />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CONSIGNMENT}
            element={
              <ProtectedRoute permission={Permissions.B2B_CONSIGNMENT_READ}>
                <Consignment />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.QUOTES}
            element={
              <ProtectedRoute permission={Permissions.B2B_QUOTE_READ}>
                <RequisitionList />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.TEAM}
            element={
              <ProtectedRoute permission={Permissions.B2B_USER_MANAGE}>
                <TeamManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.LEDGER}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <CompanyLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.FINANCE}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <FinancialHub />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ACTIVITY}
            element={
              <ProtectedRoute permission={Permissions.B2B_COMPANY_READ}>
                <ActivityLog />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ADDRESSES}
            element={
              <ProtectedRoute permission={Permissions.B2B_COMPANY_UPDATE}>
                <AddressBook />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.QUICK_ORDER}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_MANAGE}>
                <QuickOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.RMA}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <RMA />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.APPROVAL_SETTINGS}
            element={
              <ProtectedRoute permission={Permissions.B2B_COMPANY_UPDATE}>
                <ApprovalSettings />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to={AppRoutes.LOGIN} replace />} />
      </Routes>
    </Suspense>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <NotificationProvider>
                <ConfirmProvider>
                  <AuthEventInterceptor />
                  <AppContent />
                </ConfirmProvider>
              </NotificationProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>,
);
